import { useState, useEffect, useCallback, useRef } from 'react'
import { AuthUser } from '../contexts/AuthContext'
import { config } from '../config/environment'

/**
 * Shallow-compare two AuthUser objects by value.
 * Returns true if all fields match, avoiding unnecessary React state updates
 * when the backend returns identical user data on periodic token validation.
 */
function isUserEqual(a: AuthUser | null, b: AuthUser | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.displayName === b.displayName &&
    JSON.stringify(a.roles) === JSON.stringify(b.roles)
  )
}

interface AuthHookReturn {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  userRoles: string[]
  login: () => void  // synchronous — triggers full-page navigation to backend login
  logout: () => void
  validateToken: () => Promise<boolean>
  refreshToken: () => Promise<boolean>
  clearError: () => void
}

/**
 * Authentication hook for SSO-based dashboard access.
 * Login triggers a browser redirect to Cognito Hosted UI (Microsoft SSO).
 * Tokens are stored in HTTP-only cookies managed by the backend — never in localStorage.
 */
export function useAuth(apiBaseUrl?: string, oauthEnabled = false): AuthHookReturn {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const baseUrl = apiBaseUrl || config.API_BASE_URL

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Login: gated on the ros-oauth-enabled master flag.
   * - OFF (default): navigate directly to the Cognito Hosted UI (Microsoft SSO) endpoint.
   *   Backend sets PKCE cookies and redirects to Microsoft — no fetch, avoiding CORS on the
   *   cross-origin redirect. This is today's behavior, byte-identical.
   * - ON: navigate to the Ros front door instead; the backend runs the confidential OAuth flow
   *   against Ros (which federates to Rival) and lands the user back here authenticated. returnTo
   *   brings them back where they started.
   */
  const login = useCallback((): void => {
    setIsLoading(true)
    setError(null)
    if (oauthEnabled) {
      const returnTo = window.location.pathname + window.location.search
      window.location.href = `${baseUrl}/auth/ros/login?returnTo=${encodeURIComponent(returnTo)}`
    } else {
      window.location.href = `${baseUrl}/api/auth/login`
    }
  }, [baseUrl, oauthEnabled])

  /**
   * Validate: ask backend to check the HTTP-only access_token cookie.
   * Backend reads the cookie — frontend never sees the token directly.
   *
   * Only calls setUser() when the user data has actually changed, preventing
   * a cascade where new object references trigger UserIdentityContext re-fetches
   * that unmount and remount components every 5 minutes.
   */
  const validateToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${baseUrl}/api/auth/validate`, {
        credentials: 'include',
      })
      if (response.ok) {
        const { user: userData } = await response.json()
        setUser(prev => isUserEqual(prev, userData) ? prev : userData)
        return true
      }
      return false
    } catch {
      return false
    }
  }, [baseUrl])

  /**
   * Validate a Ros session (ticket 12, the front door): a user who signed in through
   * Ros has a server-side Studio session, and /auth/ros/me returns their identity.
   * This runs alongside the Cognito validate so a Ros-logged-in user is authenticated
   * for the real Studio app. Sets the user from the Ros identity when present.
   */
  const validateRosSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${baseUrl}/auth/ros/me`, { credentials: 'include' })
      if (!response.ok) return false
      // /auth/ros/me returns { success, user: { sub, email, name?, userType } }.
      const body = await response.json() as { success?: boolean; user?: { sub?: string; email?: string; name?: string; userType?: string } }
      const identity = body?.user
      if (!identity || (!identity.email && !identity.sub)) return false
      const userData = {
        id: identity.sub ?? identity.email ?? '',
        email: identity.email ?? '',
        name: identity.name ?? identity.email ?? '',
        roles: identity.userType === 'internal' ? ['user'] : ['user'],
      } as unknown as AuthUser
      setUser(prev => isUserEqual(prev, userData) ? prev : userData)
      return true
    } catch {
      return false
    }
  }, [baseUrl])

  /**
   * Refresh: ask backend to silently refresh tokens via HTTP-only cookie.
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${baseUrl}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      return response.ok
    } catch {
      return false
    }
  }, [baseUrl])

  /**
   * Logout: clear cookies on backend, redirect to Cognito logout.
   */
  const logout = useCallback(async (): Promise<void> => {
    // Clear a Ros session (ticket 12) if one exists; harmless when there is none.
    try { await fetch(`${baseUrl}/auth/ros/logout`, { method: 'POST', credentials: 'include' }) } catch { /* ignore */ }
    try {
      const response = await fetch(`${baseUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
      const { logoutUrl } = await response.json()
      setUser(null)
      window.location.href = logoutUrl
    } catch {
      // No Cognito logout (Ros-only session): clear state and return to the app root.
      setUser(null)
      window.location.href = '/'
    }
  }, [baseUrl])

  /**
   * Initialize auth state by validating the existing cookie on mount.
   *
   * The two modes are deliberately separate, NOT layered:
   * - Flag ON: the Ros session is the ONLY way in. We do not honor a lingering Cognito cookie,
   *   otherwise a user who logged in the old way before the flip would stay Cognito-authenticated
   *   and never get a ros_session (a half-Cognito / half-Ros state). No ros_session => logged out
   *   => the login screen forces them through the Ros front door, and the callback lands them back
   *   here with a ros_session. Clean cutover: everyone ends up on Ros.
   * - Flag OFF: Cognito-only, exactly like today. A stray ros_session cookie is ignored.
   */
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true)
      if (oauthEnabled) {
        const rosOk = await validateRosSession()
        if (!rosOk) setUser(null)
      } else {
        const isValid = await validateToken()
        if (!isValid) {
          const refreshed = await refreshToken()
          if (refreshed) {
            await validateToken()
          } else {
            setUser(null)
          }
        }
      }
      setIsLoading(false)
    }

    initializeAuth().catch(() => {
      setIsLoading(false)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Set up automatic silent token refresh every 5 minutes.
   * Cognito-only: the refresh endpoints are Cognito's. When the flag is ON the user is authenticated
   * by a server-side ros_session (no Cognito cookie to refresh), so polling would just churn and
   * always fail — skip it entirely.
   */
  useEffect(() => {
    if (!user || oauthEnabled) return

    const interval = setInterval(async () => {
      const isValid = await validateToken()
      if (!isValid) {
        await refreshToken()
      }
    }, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [user, validateToken, refreshToken, oauthEnabled])

  const userRoles = user?.roles ?? []

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    userRoles,
    login,
    logout,
    validateToken,
    refreshToken,
    clearError,
  }
}
